import { useState, type ReactElement, type ReactNode } from 'react';
import * as _ from 'lodash';
import {
  EditableHeading,
  Divider,
  Loader,
  Heading,
  Text,
  Button,
  Box,
  Flex,
  IconButton,
  Tooltip,
  Icon,
  Counter,
  Label,
  Dialog,
  DialogContentContainer,
  type DialogProps,
} from '@vibe/core';
import { AppModal, AppModalContent, AppModalFooter } from '@gorilla/ui/src/components/AppModal';
import {
  MoveArrowLeft,
  ExternalPage,
  Alert,
  Locked,
  Unlocked,
  Calendar,
  Form,
  Education,
  Robot,
  Formula,
  TurnInto,
  Email,
} from '@vibe/icons';

import { useParams } from 'react-router';
import { removeFieldGroupsWithBoardIds } from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/utils';
import { ErrorBox } from '@gorilla/ui/src/components/ErrorBox';
import { useSpreadsheetTemplate } from '../hooks/use-spreadsheet-template';
import { useBoards } from '../shared/hooks/use-boards';
import useMonday from '../hooks/use-monday';
import { sleep } from '../lib/utils';
import { useNavigate } from 'react-router';
import { Sheets } from './Sheets';
import FeedbackBanner from '../shared/components/FeedbackBanner';
import { CreateSpreadsheetModal } from './CreateSpreadsheetModal';
import { MessagesModal } from './MessagesModal';
import { IntroductionModal } from '../components/IntroductionModal';

type OnboardingProps = {
  dialogPosition: DialogProps['position'];
  offsetX: number;
  offsetY: number;
  showOnboardingTips: boolean;
  setShowOnboardingTips: (show: boolean) => void;
  onboardingStep: number;
  setOnboardingStep: (step: number) => void;
  step: number;
  onboardingStepsTotal: number;
  title: string;
  description: ReactNode;
  children: ReactElement;
};

function Onboarding({
  dialogPosition,
  offsetX,
  offsetY,
  showOnboardingTips,
  setShowOnboardingTips,
  onboardingStep,
  setOnboardingStep,
  step,
  onboardingStepsTotal,
  title,
  description,
  children,
}: OnboardingProps) {
  return (
    <Dialog
      id="onboarding-dialog"
      zIndex={5000}
      open={showOnboardingTips && onboardingStep === step}
      instantShowAndHide
      disableContainerScroll
      hideWhenReferenceHidden
      preventAnimationOnMount
      tooltip
      position={dialogPosition}
      moveBy={{ main: offsetX, secondary: offsetY }}
      showTrigger={[]}
      hideTrigger={[]}
      content={
        <DialogContentContainer size="large">
          <div
            style={{
              width: '300px',
            }}
          >
            <Flex gap={8}>
              <Heading
                type="h3"
                //customColor="#0073ea"
                style={{
                  margin: 0,
                  padding: 0,
                  fontWeight: 600,
                }}
              >
                {title}
              </Heading>
            </Flex>
            <Text type="text2" color="secondary" ellipsis={false} style={{ margin: '6px 0 14px 0' }}>
              {description}
            </Text>
            <Flex>
              <Text type="text2" color="primary" ellipsis={false} style={{ fontWeight: 600 }}>
                Tip {step} of {onboardingStepsTotal}
              </Text>
              <Flex gap={8} justify="end" style={{ marginLeft: 'auto' }}>
                {step < onboardingStepsTotal ? (
                  <Button
                    size="xs"
                    kind="tertiary"
                    onClick={() => {
                      _.set(window.localStorage, 'onboarding-dismissed', 'yes');
                      setShowOnboardingTips(false);
                    }}
                  >
                    Dismiss
                  </Button>
                ) : null}
                {step > 1 ? (
                  <Button
                    size="xs"
                    kind="secondary"
                    onClick={() => {
                      setOnboardingStep(onboardingStep - 1);
                    }}
                  >
                    Back
                  </Button>
                ) : null}
                <Button
                  size="xs"
                  kind="primary"
                  onClick={() => {
                    if (step < onboardingStepsTotal) {
                      setOnboardingStep(onboardingStep + 1);
                    } else {
                      _.set(window.localStorage, 'onboarding-dismissed', 'yes');
                      setShowOnboardingTips(false);
                    }
                  }}
                >
                  {step < onboardingStepsTotal ? 'Next' : 'Got it'}
                </Button>
              </Flex>
            </Flex>
          </div>
        </DialogContentContainer>
      }
    >
      {children}
    </Dialog>
  );
}

function getMissingBoardIds(boardIds: number[], foundBoards: { id: number }[]) {
  return boardIds.filter((boardId) => !foundBoards.find((board) => board.id === boardId));
}

export function EditTemplate() {
  const params = useParams();
  const navigate = useNavigate();
  const { mondayContext } = useMonday();
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [showBoardSelectionModal, setShowBoardSelectionModal] = useState(false);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  //const [showCreateSpreadsheetModal, setShowCreateSpreadsheetModal] = useState(false);
  const [showCreateSpreadsheetModal, setShowCreateSpreadsheetModal] = useState<any>(false);
  const [showIntroductionModal, setShowIntroductionModal] = useState<boolean>(false);
  const {
    template,
    config,
    error,
    isUpdatingTemplate,
    updateTemplateDraft,
    updateTemplateDraftConfig,
    saveTemplate,
    deleteTemplate,
    isLoading,
    hasDraft,
    referencedBoardIds,
    isDeletingTemplate,
  } = useSpreadsheetTemplate(parseInt(params.templateId!, 10));
  const { boards, error: boardsError } = useBoards(referencedBoardIds, false);
  const missingBoardIds = boards ? getMissingBoardIds(referencedBoardIds, boards) : [];

  const [showOnboardingTips, setShowOnboardingTips] = useState<boolean>(window.localStorage.getItem('onboarding-dismissed') !== 'yes');
  const [onboardingStep, setOnboardingStep] = useState<number>(1);
  const onboardingStepsTotal = 5;

  const hasError = !!boardsError;
  let content;

  if (boardsError) {
    content = <ErrorBox error={new Error('We are unable to load some of the boards used in this template.')} />;
  } else if (config && missingBoardIds.length) {
    content = (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 'calc(100vh - 150px)' }}>
          <div style={{ textAlign: 'center' }}>
            <div>
              {missingBoardIds.length < 2 ? (
                <>
                  <h1>Missing board</h1>
                  <p>
                    The following board used in this template either doesn't
                    <br />
                    exist anymore or you lack permissions to access it:
                  </p>
                </>
              ) : (
                <>
                  <h1>Missing boards</h1>
                  <p>
                    The following boards used in this template either don't exist
                    <br />
                    anymore or you lack permissions to access them:
                  </p>
                </>
              )}
              <Flex gap={8} justify="center">
                {missingBoardIds.map((id, index) => (
                  <Label key={index} text={`Board ID: ${id}`} color="dark" kind="line" />
                ))}
              </Flex>
            </div>
            <div style={{ marginTop: '40px' }}>
              <Button
                onClick={() => {
                  updateTemplateDraftConfig(removeFieldGroupsWithBoardIds(config, missingBoardIds));
                }}
              >
                {missingBoardIds.length < 2 ? <>Remove board</> : <>Remove boards</>}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  } else if (config && template) {
    content = (
      <>
        <Onboarding
          dialogPosition="top-start"
          offsetX={-2}
          offsetY={-30}
          showOnboardingTips={showOnboardingTips}
          setShowOnboardingTips={setShowOnboardingTips}
          step={5}
          onboardingStepsTotal={onboardingStepsTotal}
          onboardingStep={onboardingStep}
          setOnboardingStep={setOnboardingStep}
          title="Custom formulas"
          description={
            <>
              Swap the column type to <Icon icon={Formula} style={{ transform: 'translate3d(0,3px,0)' }} /> formula and apply its results in
              exports. Or use it to <Icon icon={TurnInto} style={{ transform: 'translate3d(0,3px,0)' }} /> update item columns across
              boards. Learn more{' '}
              <a href="https://www.youtube.com/watch?v=zgDFN3FS4qA" target="_blank" rel="noopener noreferrer">
                here
              </a>
              .
            </>
          }
        >
          <div
            style={{
              position: 'absolute',
              top: '200px',
              left: '110px',
              width: '50px',
              height: '50px',
            }}
          ></div>
        </Onboarding>

        <Onboarding
          dialogPosition="top"
          offsetX={-175}
          offsetY={0}
          showOnboardingTips={showOnboardingTips}
          setShowOnboardingTips={setShowOnboardingTips}
          step={4}
          onboardingStepsTotal={onboardingStepsTotal}
          onboardingStep={onboardingStep}
          setOnboardingStep={setOnboardingStep}
          title="Spreadsheet configuration"
          description={
            <>
              This is where you manage and map your board columns, add worksheets, combine boards and apply custom formulas. Learn more{' '}
              <a href="https://getgorilla.app/products/spreadsheet-gorilla/docs/templates" target="_blank" rel="noopener noreferrer">
                here
              </a>
              .
            </>
          }
        >
          <Sheets
            boards={boards}
            template={template}
            onSave={() => {
              saveTemplate();
            }}
            config={config}
            onSheetAdd={(newSheet) => {
              updateTemplateDraftConfig({ ...config, sheets: [...config.sheets, newSheet] });
            }}
            onSheetRemove={(sheetId) => {
              updateTemplateDraftConfig({ ...config, sheets: config.sheets.filter((sheet) => sheetId !== sheet.id) });
            }}
            onSheetChange={(changedSheet) => {
              updateTemplateDraftConfig({
                ...config,
                sheets: config.sheets.map((sheet) => (sheet.id === changedSheet.id ? changedSheet : sheet)),
              });
            }}
            onOpenBoardManager={() => {
              setShowBoardSelectionModal(true);
            }}
          />
        </Onboarding>

        {/* CTA */}

        <div
          style={{
            margin: '32px 32px 32px 32px',
          }}
        >
          <Flex gap={32} align="stretch" direction="row">
            <div
              style={{
                display: 'flex',
                alignItems: 'stretch',
                width: '50%',
              }}
            >
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  padding: '32px',
                  width: '100%',
                  backgroundColor: '#f6f7fb',
                  boxShadow: '0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #d0d4e4',
                  borderRadius: '8px',
                  color: '#323338',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    right: '20%',
                    bottom: '-50px',
                    color: 'rgba(0,0,0,0.03)',
                  }}
                >
                  <Icon icon={Calendar} size={150} ignoreFocusStyle />
                </div>
                <Flex gap={20} justify="center" align="end">
                  <div>
                    <Flex gap={15}>
                      <div>
                        <Flex gap={5}>
                          <Icon icon={Calendar} size={18} ignoreFocusStyle />
                          <span>Demo</span>
                        </Flex>
                        <h5 style={{ marginTop: '10px' }}>
                          Book a demo and <br />
                          talk to an expert
                        </h5>
                      </div>
                    </Flex>
                  </div>
                  <div
                    style={{
                      marginLeft: 'auto',
                    }}
                  >
                    <IconButton
                      icon={ExternalPage}
                      size="medium"
                      kind="secondary"
                      color="primary"
                      onClick={() => {
                        window.open('https://cal.com/gorilla-apps/spreadsheet-gorilla-demo', '_blank');
                      }}
                    />
                  </div>
                </Flex>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'stretch',
                width: '50%',
              }}
            >
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  padding: '32px',
                  width: '100%',
                  backgroundColor: '#f6f7fb',
                  boxShadow: '0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #d0d4e4',
                  borderRadius: '8px',
                  color: '#323338',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    right: '20%',
                    bottom: '-50px',
                    color: 'rgba(0,0,0,0.03)',
                  }}
                >
                  <Icon icon={Form} size={150} ignoreFocusStyle />
                </div>
                <Flex gap={20} justify="center" align="end">
                  <div>
                    <Flex gap={15}>
                      <div>
                        <Flex gap={5}>
                          <Icon icon={Form} size={18} ignoreFocusStyle />
                          <span>Support</span>
                        </Flex>
                        <h5 style={{ marginTop: '10px' }}>
                          We're here to help and <br />
                          answer any question
                        </h5>
                      </div>
                    </Flex>
                  </div>
                  <div
                    style={{
                      marginLeft: 'auto',
                    }}
                  >
                    <IconButton
                      icon={ExternalPage}
                      size="medium"
                      kind="secondary"
                      color="primary"
                      onClick={() => {
                        window.open('https://getgorilla.app/contact', '_blank');
                      }}
                    />
                  </div>
                </Flex>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'stretch',
                width: '50%',
              }}
            >
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  padding: '32px',
                  width: '100%',
                  backgroundColor: '#f6f7fb',
                  boxShadow: '0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #d0d4e4',
                  borderRadius: '8px',
                  color: '#323338',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    right: '20%',
                    bottom: '-60px',
                    color: 'rgba(0,0,0,0.03)',
                  }}
                >
                  <Icon icon={Education} size={150} ignoreFocusStyle />
                </div>
                <Flex gap={20} justify="center" align="end">
                  <div>
                    <Flex gap={15}>
                      <div>
                        <Flex gap={5}>
                          <Icon icon={Education} size={18} ignoreFocusStyle />
                          <span>Documentation</span>
                        </Flex>
                        <h5 style={{ marginTop: '10px' }}>
                          Learn how to use and <br />
                          troubleshoot the app
                        </h5>
                      </div>
                    </Flex>
                  </div>
                  <div
                    style={{
                      marginLeft: 'auto',
                    }}
                  >
                    <IconButton
                      icon={ExternalPage}
                      size="medium"
                      kind="secondary"
                      color="primary"
                      onClick={() => {
                        window.open('https://getgorilla.app/products/spreadsheet-gorilla/how-to-use', '_blank');
                      }}
                    />
                  </div>
                </Flex>
              </div>
            </div>
          </Flex>
        </div>
      </>
    );
  } else {
    content = (
      <>
        <div
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '32px',
            width: '100%',
            height: 'calc(100vh - 210px)',
          }}
        >
          <Loader size="small" color="dark" />
        </div>
      </>
    );
  }

  return (
    <>
      {showCreateSpreadsheetModal ? (
        <CreateSpreadsheetModal
          onClose={() => setShowCreateSpreadsheetModal(false)}
          show={!!showCreateSpreadsheetModal}
          initialCtx={{ templateId: template?.id, disableTemplateSelection: true, formatId: 'excel', showSelection: true }}
        />
      ) : null}
      {showIntroductionModal ? <IntroductionModal show={showIntroductionModal} onClose={() => setShowIntroductionModal(false)} /> : null}
      {showMessagesModal && template ? (
        <MessagesModal
          show={showMessagesModal}
          onClose={async (newConfig) => {
            if (newConfig) {
              saveTemplate({ ...template, config: newConfig });
            }
            setShowMessagesModal(false);
          }}
          template={template}
        />
      ) : null}
      <FeedbackBanner />
      {/* HEADER */}
      {/* zIndex: 1000 doesn't work with tooltips */}
      <div
        style={{
          zIndex: 1000,
          position: 'sticky',
          top: 0,
          boxShadow: '0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div
          style={{
            padding: '16px',
            backgroundColor: '#ffffff',
          }}
        >
          <Flex gap="medium" align="center">
            <div
              style={{
                textAlign: 'left',
              }}
            >
              <Flex gap={10}>
                <IconButton
                  kind="tertiary"
                  size="medium"
                  icon={MoveArrowLeft}
                  onClick={() => {
                    navigate(`/`);
                  }}
                />
                <Tooltip content="Save template" zIndex={2000}>
                  <Button
                    onClick={() => {
                      saveTemplate();
                    }}
                    disabled={!hasDraft}
                    loading={isUpdatingTemplate}
                    size="medium"
                    kind="secondary"
                  >
                    Save
                  </Button>
                </Tooltip>
                <Tooltip content="Delete template" zIndex={2000}>
                  <Button
                    color="primary"
                    size="medium"
                    kind="tertiary"
                    onClick={() => {
                      setShowConfirmDeleteModal(true);
                    }}
                  >
                    Delete
                  </Button>
                </Tooltip>
                <AppModal
                  data-testid="confirm-delete-modal"
                  show={showConfirmDeleteModal}
                  onClose={() => {
                    setShowConfirmDeleteModal(false);
                  }}
                  width="default"
                  alertDialog={true}
                >
                  <AppModalContent>
                    <Flex direction="row" align="center" gap={4} style={{ marginBottom: '24px' }}>
                      <Flex gap={8}>
                        <Icon icon={Alert} size={20} />
                        <div style={{ maxWidth: '200px' }}>
                          <Text type="text1" weight="medium" ellipsis>
                            Warning
                          </Text>
                        </div>
                      </Flex>
                    </Flex>
                    <Box marginTop="medium">
                      You are about to delete the spreadsheet template <strong>{template?.name}</strong> for everyone on your team. Once
                      deleted, the template can't be restored. Are you sure to proceed?
                    </Box>
                  </AppModalContent>
                  <AppModalFooter>
                    <Flex gap={12} justify="end">
                      <Button
                        kind="tertiary"
                        onClick={() => {
                          setShowConfirmDeleteModal(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        loading={isDeletingTemplate}
                        onClick={async () => {
                          await deleteTemplate();
                          setShowConfirmDeleteModal(false);
                          navigate('/');
                        }}
                      >
                        Confirm
                      </Button>
                    </Flex>
                  </AppModalFooter>
                </AppModal>
              </Flex>
            </div>
            <Onboarding
              dialogPosition="bottom"
              offsetX={0}
              offsetY={0}
              showOnboardingTips={showOnboardingTips}
              setShowOnboardingTips={setShowOnboardingTips}
              step={1}
              onboardingStepsTotal={onboardingStepsTotal}
              onboardingStep={onboardingStep}
              setOnboardingStep={setOnboardingStep}
              title="Template name"
              description="Give your spreadsheet template a fitting name and optionally share it across all board views with your team or keep it private."
            >
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                }}
              >
                <Flex gap={0} align="center" justify="center">
                  <div>
                    <EditableHeading
                      onChange={(value) => {
                        updateTemplateDraft({ name: value });
                      }}
                      type="h3"
                      value={template?.name || 'Template'}
                      tooltipProps={{
                        zIndex: 2000,
                      }}
                    />
                  </div>
                  {_.toNumber(_.get(mondayContext, 'user.id')) === _.get(template, 'creatorId') ? (
                    <Flex align="center">
                      <Tooltip
                        content={
                          template?.private
                            ? 'Private template: Click to share it across all board views'
                            : 'Shared template: Click to make it private'
                        }
                        zIndex={2000}
                      >
                        <IconButton
                          disabled={boardsError}
                          size="medium"
                          kind="tertiary"
                          icon={template?.private ? Locked : Unlocked}
                          onClick={(ev) => {
                            updateTemplateDraft({ private: !template?.private });
                          }}
                        />
                      </Tooltip>
                    </Flex>
                  ) : (
                    <></>
                  )}
                </Flex>
              </div>
            </Onboarding>
            <Onboarding
              dialogPosition="left"
              offsetX={10}
              offsetY={45}
              showOnboardingTips={showOnboardingTips}
              setShowOnboardingTips={setShowOnboardingTips}
              step={2}
              onboardingStepsTotal={onboardingStepsTotal}
              onboardingStep={onboardingStep}
              setOnboardingStep={setOnboardingStep}
              title="Spreadsheet preview and export"
              description="Open the spreadsheet preview to double check the outcome of your configuration. Afterwards manually export your template as XLS or CSV."
            >
              <div
                style={{
                  textAlign: 'right',
                }}
              >
                <Flex gap={12} justify="end">
                  {config && missingBoardIds.length ? (
                    <>
                      <Button disabled size="medium" kind="secondary">
                        Preview
                      </Button>
                      <Button disabled size="medium" kind="primary">
                        Export
                      </Button>
                    </>
                  ) : (
                    <>
                      <Tooltip content="Preview spreadsheet" zIndex={2000}>
                        <Button
                          disabled={hasError}
                          onClick={async () => {
                            await saveTemplate();
                            await sleep(1000); // temp fix for loading of preview sometimes failing
                            navigate(`/template/${params.templateId}/render`);
                          }}
                          size="medium"
                          kind="secondary"
                        >
                          Preview
                        </Button>
                      </Tooltip>
                      <Tooltip content="Export spreadsheet" zIndex={2000}>
                        <Button
                          disabled={hasError}
                          onClick={async () => {
                            await saveTemplate();
                            //await sleep(1000); // temp fix for loading of preview sometimes failing
                            //navigate(`/template/${params.templateId}/render`);
                            setShowCreateSpreadsheetModal(true);
                          }}
                          size="medium"
                          kind="primary"
                        >
                          Export
                        </Button>
                      </Tooltip>
                    </>
                  )}
                </Flex>
              </div>
            </Onboarding>
          </Flex>
        </div>
        <Divider withoutMargin />
      </div>
      <div style={{ position: 'relative' }}>
        {!boardsError && !missingBoardIds.length ? (
          <div
            style={{
              position: 'absolute',
              top: '25px',
              right: '32px',
            }}
          >
            <Onboarding
              dialogPosition="left"
              offsetX={0}
              offsetY={0}
              showOnboardingTips={showOnboardingTips}
              setShowOnboardingTips={setShowOnboardingTips}
              step={3}
              onboardingStepsTotal={onboardingStepsTotal}
              onboardingStep={onboardingStep}
              setOnboardingStep={setOnboardingStep}
              title="Exporting with automations"
              description={
                <>
                  The message manager is where you create the email messages for your automations. Learn more about automated exports{' '}
                  <a href="https://getgorilla.app/products/spreadsheet-gorilla/automations" target="_blank" rel="noopener noreferrer">
                    here
                  </a>
                  .
                </>
              }
            >
              <Flex>
                <Tooltip content="Edit email messages for your automations" zIndex={2000}>
                  <Button
                    disabled={boardsError || missingBoardIds.length > 0}
                    size="medium"
                    kind="tertiary"
                    leftIcon={Email}
                    onClick={() => {
                      setShowMessagesModal(true);
                    }}
                  >
                    Message manager &nbsp;{' '}
                    <Counter count={config?.messages ? config?.messages.length : 0} size="large" color="dark" className="inlinecounter" />
                  </Button>
                </Tooltip>
              </Flex>
            </Onboarding>
          </div>
        ) : null}

        {content}
      </div>
    </>
  );
}
